import React, { useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../services/api';

/** Minimal TopoJSON → GeoJSON polygon decoder (countries-110m). */
function decodeTopoCountries(topology) {
  if (!topology?.arcs || !topology?.objects?.countries) return [];
  const { arcs, transform } = topology;
  const tx = transform
    ? (x, y) => [x * transform.scale[0] + transform.translate[0], y * transform.scale[1] + transform.translate[1]]
    : (x, y) => [x, y];

  const decodeArc = (idx) => {
    const reverse = idx < 0;
    const arc = arcs[reverse ? ~idx : idx];
    let x = 0;
    let y = 0;
    const pts = [];
    for (const d of arc) {
      x += d[0];
      y += d[1];
      pts.push(tx(x, y));
    }
    return reverse ? pts.reverse() : pts;
  };

  const ring = (indexes) => {
    const pts = [];
    for (const i of indexes) {
      const seg = decodeArc(i);
      if (!pts.length) pts.push(...seg);
      else pts.push(...seg.slice(1));
    }
    return pts;
  };

  const polys = [];
  for (const g of topology.objects.countries.geometries || []) {
    if (!g) continue;
    if (g.type === 'Polygon') {
      polys.push(g.arcs.map(ring));
    } else if (g.type === 'MultiPolygon') {
      for (const p of g.arcs) polys.push(p.map(ring));
    }
  }
  return polys;
}

function project(lng, lat, width, height) {
  const x = ((lng + 180) / 360) * width;
  const y = ((90 - lat) / 180) * height;
  return [x, y];
}

function HeatMap({ points = [] }) {
  const canvasRef = useRef(null);
  const landRef = useRef(null);
  const [landReady, setLandReady] = useState(0);
  const maxW = useMemo(() => Math.max(1, ...points.map((p) => Number(p.weight) || 1)), [points]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/admin/world-110m.json');
        if (!resp.ok) return;
        const topo = await resp.json();
        if (cancelled) return;
        landRef.current = decodeTopoCountries(topo);
        setLandReady((n) => n + 1);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth || 640;
      const height = Math.round(width * 0.48);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      // Ocean
      const ocean = ctx.createLinearGradient(0, 0, 0, height);
      ocean.addColorStop(0, '#0c1929');
      ocean.addColorStop(1, '#0a1220');
      ctx.fillStyle = ocean;
      ctx.fillRect(0, 0, width, height);

      // Landmasses
      const land = landRef.current;
      if (Array.isArray(land) && land.length) {
        ctx.fillStyle = '#1e293b';
        ctx.strokeStyle = 'rgba(148,163,184,0.25)';
        ctx.lineWidth = 0.5;
        for (const poly of land) {
          ctx.beginPath();
          for (let ri = 0; ri < poly.length; ri++) {
            const ring = poly[ri];
            if (!ring?.length) continue;
            for (let i = 0; i < ring.length; i++) {
              const [lng, lat] = ring[i];
              const [x, y] = project(lng, lat, width, height);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.closePath();
          }
          ctx.fill();
          ctx.stroke();
        }
      } else {
        // Fallback graticule while map loads
        ctx.strokeStyle = 'rgba(148,163,184,0.12)';
        for (let lon = -180; lon <= 180; lon += 30) {
          const x = ((lon + 180) / 360) * width;
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
        }
        for (let lat = -60; lat <= 60; lat += 30) {
          const y = ((90 - lat) / 180) * height;
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
        }
      }

      // Heat points
      for (const p of points) {
        const lat = Number(p.lat);
        const lng = Number(p.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const [x, y] = project(lng, lat, width, height);
        const w = Number(p.weight) || 1;
        const r = 4 + (Math.sqrt(w) / Math.sqrt(maxW)) * 22;
        const alpha = 0.28 + (w / maxW) * 0.55;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(52,211,153,${alpha})`);
        g.addColorStop(0.45, `rgba(16,185,129,${alpha * 0.55})`);
        g.addColorStop(1, 'rgba(16,185,129,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [points, maxW, landReady]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded-lg border border-white/10 bg-[#050807]"
      style={{ height: 'auto', minHeight: 220 }}
    />
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-lg border border-white/10 bg-[#050807]/60">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-100 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function locationLabel(r) {
  return [r.city, r.region, r.country].filter(Boolean).join(', ') || '—';
}

export default function AdminVisitorsTab({ refreshTick = 0 }) {
  const [days, setDays] = useState(30);
  const [surface, setSurface] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError('');
    try {
      const resp = await ApiService.getAdminVisitors({ days, surface });
      setData(resp);
    } catch (e) {
      if (!quiet) setError(e?.message || 'Failed to load visitors');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, surface]);

  useEffect(() => {
    if (!refreshTick) return;
    load({ quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const totals = data?.totals || { pageviews: 0, uniqueVisitors: 0, countries: 0 };
  const maxDay = Math.max(1, ...(data?.byDay || []).map((d) => d.pageviews || 0));
  const cities = data?.byCity || [];
  const heatWithGeo = (data?.heat || []).filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)));

  return (
    <div className="space-y-4">
      <div className="app-panel p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Visitors</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              First-party pageviews with IP + geo (CloudFront / GeoIP). Retained 90 days.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={surface}
              onChange={(e) => setSurface(e.target.value)}
              className="text-xs px-2 py-1.5 rounded bg-white/5 border border-white/15"
            >
              <option value="all">All surfaces</option>
              <option value="marketing">Marketing</option>
              <option value="app">App / tenants</option>
              <option value="admin">Admin</option>
            </select>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="text-xs px-2 py-1.5 rounded bg-white/5 border border-white/15"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            <button
              type="button"
              disabled={loading}
              onClick={load}
              className="text-xs px-2 py-1.5 rounded bg-white/5 border border-white/15"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && <div className="mt-3 text-xs text-amber-300">{error}</div>}

        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Pageviews" value={totals.pageviews?.toLocaleString?.() ?? totals.pageviews} />
          <Stat label="Unique visitors" value={totals.uniqueVisitors?.toLocaleString?.() ?? totals.uniqueVisitors} />
          <Stat label="Countries" value={totals.countries?.toLocaleString?.() ?? totals.countries} />
          <Stat label="Cities" value={cities.length.toLocaleString()} />
          <Stat label="Range" value={`${days}d`} />
        </div>

        {data?.posthog && (
          <div className="mt-3 text-xs text-slate-400 flex flex-wrap items-center gap-2">
            <span className={`inline-flex px-1.5 py-0.5 rounded border ${data.posthog.configured ? 'border-teal-500/40 text-teal-300 bg-teal-500/10' : 'border-white/15 text-slate-500'}`}>
              PostHog {data.posthog.configured ? 'configured' : 'not configured'}
            </span>
            <a
              href={data.posthog.dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-400 hover:underline"
            >
              Open PostHog →
            </a>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">World heat map</h3>
          <HeatMap points={heatWithGeo} />
          <p className="text-[11px] text-slate-500 mt-2">
            World landmasses (Natural Earth 110m) · equirectangular · glow size ∝ pageviews
            {heatWithGeo.length === 0 ? ' · no lat/lng points yet (geo may be city/country-only)' : ` · ${heatWithGeo.length} geo points`}
          </p>
        </div>
        <div className="app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">By day</h3>
          <div className="space-y-1 max-h-72 overflow-auto pr-1">
            {(data?.byDay || []).length === 0 && (
              <div className="text-xs text-slate-500">No pageviews in range yet.</div>
            )}
            {(data?.byDay || []).map((d) => (
              <div key={d.day} className="text-xs">
                <div className="flex justify-between text-slate-400 mb-0.5">
                  <span>{d.day}</span>
                  <span className="tabular-nums">{d.pageviews} · {d.uniqueVisitors} uv</span>
                </div>
                <div className="h-1.5 rounded bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/70"
                    style={{ width: `${Math.max(4, (d.pageviews / maxDay) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">Countries</h3>
          <div className="overflow-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">Country</th><th className="text-right">PV</th><th className="text-right">UV</th></tr>
              </thead>
              <tbody>
                {(data?.byCountry || []).map((r) => (
                  <tr key={r.country} className="border-t border-white/10">
                    <td className="py-1.5">{r.country || '—'}</td>
                    <td className="text-right tabular-nums">{r.pageviews}</td>
                    <td className="text-right tabular-nums text-slate-400">{r.uniqueVisitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">States / regions</h3>
          <div className="overflow-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">Region</th><th className="text-left">Country</th><th className="text-right">PV</th></tr>
              </thead>
              <tbody>
                {(data?.byRegion || []).map((r) => (
                  <tr key={`${r.country}-${r.region}`} className="border-t border-white/10">
                    <td className="py-1.5">{r.region || '—'}</td>
                    <td className="text-slate-400">{r.country || '—'}</td>
                    <td className="text-right tabular-nums">{r.pageviews}</td>
                  </tr>
                ))}
                {(data?.byRegion || []).length === 0 && (
                  <tr><td colSpan={3} className="py-2 text-slate-500">No region data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">Cities</h3>
          <div className="overflow-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">City</th><th className="text-left">Region</th><th className="text-left">Country</th><th className="text-right">PV</th></tr>
              </thead>
              <tbody>
                {cities.map((r) => (
                  <tr key={`${r.country}-${r.region}-${r.city}`} className="border-t border-white/10">
                    <td className="py-1.5">{r.city || '—'}</td>
                    <td className="text-slate-400">{r.region || '—'}</td>
                    <td className="text-slate-400">{r.country || '—'}</td>
                    <td className="text-right tabular-nums">{r.pageviews}</td>
                  </tr>
                ))}
                {cities.length === 0 && (
                  <tr><td colSpan={4} className="py-2 text-slate-500">No city data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="app-panel p-4">
          <h3 className="text-sm font-semibold mb-2">Top paths</h3>
          <div className="overflow-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">Path</th><th className="text-right">PV</th></tr>
              </thead>
              <tbody>
                {(data?.byPath || []).map((r) => (
                  <tr key={r.path} className="border-t border-white/10">
                    <td className="py-1.5 font-mono truncate max-w-[12rem]" title={r.path}>{r.path}</td>
                    <td className="text-right tabular-nums">{r.pageviews}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="app-panel p-4">
        <h3 className="text-sm font-semibold mb-2">Recent visits</h3>
        <div className="overflow-auto max-h-96">
          <table className="w-full text-xs">
            <thead className="text-slate-500 sticky top-0 bg-surface-1">
              <tr>
                <th className="text-left py-1 pr-2">When</th>
                <th className="text-left pr-2">IP</th>
                <th className="text-left pr-2">City</th>
                <th className="text-left pr-2">State / region</th>
                <th className="text-left pr-2">Country</th>
                <th className="text-left pr-2">Surface</th>
                <th className="text-left pr-2">Host</th>
                <th className="text-left">Path</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent || []).map((r, i) => (
                <tr key={`${r.ts}-${i}`} className="border-t border-white/10 align-top" title={locationLabel(r)}>
                  <td className="py-1.5 pr-2 whitespace-nowrap text-slate-400">
                    {r.ts ? new Date(r.ts).toLocaleString() : '—'}
                  </td>
                  <td className="pr-2 font-mono text-slate-300">{r.ipDisplay || '—'}</td>
                  <td className="pr-2">{r.city || '—'}</td>
                  <td className="pr-2 text-slate-400">{r.region || '—'}</td>
                  <td className="pr-2">{r.country || '—'}</td>
                  <td className="pr-2 text-slate-400">{r.surface || '—'}</td>
                  <td className="pr-2 font-mono text-slate-500 truncate max-w-[8rem]" title={r.host}>{r.host || '—'}</td>
                  <td className="font-mono truncate max-w-xs" title={r.path}>{r.path}</td>
                </tr>
              ))}
              {(data?.recent || []).length === 0 && (
                <tr><td colSpan={8} className="py-3 text-slate-500">No visits recorded yet. Browse testera.io / app after deploy.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
